import React, { FunctionComponent } from 'react'
import { Divider, Grid, Heading, Text, usePercent } from '@afterpaytouch/core'
import { Money } from '../Money'
import { MoneyPrefix } from '../Money/types'
import { useCheckoutSizes } from '../../utils/responsive'
import { useTranslation } from '../../utils/convergence'
import { useAppRouter, useIsAuNonAppleRetailCheckout, useIsConsumerLendingOrder, useSelectedPaymentOption } from '../../state'
import styles from './style.module.css'

export const ConsumerLendingSummaryBreakdown: FunctionComponent = () => {
  const { t } = useTranslation()
  const { sectionHeadingSize, textSize } = useCheckoutSizes()
  const selectedPaymentOption = useSelectedPaymentOption()
  const isConsumerLending = useIsConsumerLendingOrder()
  const { locale } = useAppRouter()
  const { formatPercent } = usePercent({ locale, decimalPlaces: 2, shouldDivideBy100: true })
  const isAuNonAppleRetailCheckout = useIsAuNonAppleRetailCheckout()

  if (selectedPaymentOption === undefined || !isConsumerLending) {
    return null
  }

  const { interestAmount, totalAmount, amountDueToday, apr, purchaseAmount, repaymentAmount } = selectedPaymentOption

  const hasDownPayment = parseFloat(amountDueToday.amount) > 0
  const showDownPaymentRow = isAuNonAppleRetailCheckout && hasDownPayment
  const totalRowAmount = isAuNonAppleRetailCheckout && repaymentAmount !== null ? repaymentAmount : totalAmount

  return (
    <>
      <div className={styles.heading}>
        <Heading size={sectionHeadingSize} renderAs='h2'>
          {t('consumerLending:summary:breakdownTitle')}
        </Heading>
      </div>
      <div className={styles.amountsGroup}>
        {purchaseAmount !== undefined && (
          <Grid>
            <Text size={textSize} renderAs='span'>
              {t('consumerLending:summary:purchaseAmount')}
            </Text>
            <Grid.Item sm='auto' data-testid='clSummaryBreakdown-purchaseAmount'>
              <Money value={purchaseAmount} size={textSize} />
            </Grid.Item>
          </Grid>
        )}
        {showDownPaymentRow && (
          <Grid>
            <Text size={textSize} renderAs='span'>
              {isAuNonAppleRetailCheckout ? t('consumerLending:summary:downPayment', { context: 'auPm' }) : t('consumerLending:summary:downPayment')}
            </Text>
            <Grid.Item sm='auto' data-testid='clSummaryBreakdown-downPayment'>
              <Money value={amountDueToday} size={textSize} prefix={MoneyPrefix.NEGATIVE} />
            </Grid.Item>
          </Grid>
        )}
        {showDownPaymentRow && interestAmount !== null && (
          <div className={styles.groupDivider}>
            <Divider kind='Hairline' margin='No' />
          </div>
        )}
        {interestAmount !== null && (
          <Grid>
            <Text size={textSize} renderAs='span'>
              {isAuNonAppleRetailCheckout
                ? t('consumerLending:summary:interestWithApr', { apr: formatPercent(apr ?? 0), context: 'auPm' })
                : t('consumerLending:summary:interestWithApr', { apr: formatPercent(apr ?? 0) })}
            </Text>
            <Grid.Item sm='auto' data-testid='clSummaryBreakdown-interestAmount'>
              <Money value={interestAmount} size={textSize} />
            </Grid.Item>
          </Grid>
        )}
      </div>
      <div className={styles.groupDivider}>
        <Divider kind='Hairline' margin='No' />
      </div>
      <div className={styles.amountsGroup}>
        <Grid>
          <Text size={textSize} renderAs='span'>
            {isAuNonAppleRetailCheckout
              ? t('consumerLending:summary:totalAmountPayable', { context: 'auPm' })
              : t('consumerLending:summary:totalAmountPayable')}
          </Text>
          <Grid.Item sm='auto' data-testid='clSummaryBreakdown-totalAmount'>
            <Money value={totalRowAmount} size={textSize} />
          </Grid.Item>
        </Grid>
        <Grid>
          <Text size={textSize} renderAs='span' bold>
            {isAuNonAppleRetailCheckout ? t('consumerLending:summary:dueToday', { context: 'auPm' }) : t('consumerLending:summary:dueToday')}
          </Text>
          <Grid.Item sm='auto' data-testid='clSummaryBreakdown-amountDueToday'>
            <Money value={amountDueToday} size={textSize} bold />
          </Grid.Item>
        </Grid>
      </div>
    </>
  )
}
